export const SITE = {
  name: 'Image Enlarger',
  domain: 'https://imageenlarger.co',
  supportEmail: 'support@imageenlarger.co',
  launchDate: '2026-07-26',
} as const;

export type PageDef = {
  path: string;
  label: string;
  title: string;
  description: string;
  h1: string;
  subhead: string;
  index: boolean;
  uploadLabel: string;
  angle?: string;
};

export const PAGES: PageDef[] = [
  {
    path: '/',
    label: 'Home',
    title: 'AI Image Enlarger Online - Enlarge Images Without Losing Quality',
    description:
      'Upscale and enlarge images with AI. No sign-up required. Free outputs include a watermark; Pro removes the watermark.',
    h1: 'Free AI Image Upscaler & Enlarger',
    subhead:
      'Upload any JPG, PNG, or WebP image and enlarge it with AI. No account needed. Free outputs include a watermark; upgrade to Pro for watermark-free HD.',
    index: true,
    uploadLabel: 'Upload Image',
  },
  {
    path: '/ai-image-upscaler',
    label: 'AI Upscaler',
    title: 'AI Image Upscaler — Upscale Images Online with AI',
    description:
      'Use our AI image upscaler to enlarge photos and graphics online. No sign-up needed for free use. Upgrade to Pro for 4K output.',
    h1: 'AI Image Upscaler Online',
    subhead:
      'Enlarge low-resolution images with AI. Perfect for product photos, portraits, and social posts. Start free, upgrade for HD.',
    index: true,
    uploadLabel: 'Upload Image',
    angle: 'ai',
  },
  {
    path: '/ai-image-enlarger',
    label: 'AI Enlarger',
    title: 'AI Image Enlarger — Enlarge Photos Without Losing Detail',
    description:
      'Enlarge photos and images online with AI. Free daily quota, no sign-up. Free outputs include a watermark; Pro is watermark-free. Upgrade to Pro for HD and 4× enlargement.',
    h1: 'AI Image Enlarger Online',
    subhead:
      'Make your photos bigger while keeping edges sharp. Our AI image enlarger is designed for portraits, products, and prints.',
    index: true,
    uploadLabel: 'Upload Image',
    angle: 'enlarge',
  },
  {
    path: '/free-image-upscaler',
    label: 'Free Upscaler',
    title: 'Free Image Upscaler — 2× Daily, No Sign-Up, Watermark-Free Pro',
    description:
      'Upscale images for free with our AI image upscaler. No sign-up, 2 images/day. Free outputs include a watermark; Pro is watermark-free. Upgrade to Pro for HD and 4× output.',
    h1: 'Free Image Upscaler Online',
    subhead:
      'Get a higher-resolution image without paying. No account required. Free outputs include a watermark. Free users get 2 upscales per day.',
    index: true,
    uploadLabel: 'Upload Image Free',
    angle: 'free',
  },
  {
    path: '/image-upscaler-4k',
    label: '4K Upscaler',
    title: '4K AI Image Upscaler — 4× Pro Planned, Free 2× Preview',
    description:
      'Learn about 4K and 8K AI image upscaling with Image Enlarger. Free preview uses 2×; 4× high-resolution output is a planned Pro feature.',
    h1: '4K AI Image Upscaler',
    subhead:
      'Turn small images into 4K-ready visuals. Free uses 2×; Pro will unlock 4× and 8K-ready output for prints, presentations, and high-resolution screens.',
    index: true,
    uploadLabel: 'Upload Image',
    angle: '4k',
  },
  {
    path: '/enlarge-photo-online',
    label: 'Enlarge Photo',
    title: 'Enlarge Photo Online — Free AI Photo Enlarger',
    description:
      'Enlarge photos online with AI. No sign-up, free daily quota. Free outputs include a watermark; Pro is watermark-free. Upgrade to Pro for HD and 4× photo enlargement.',
    h1: 'Enlarge Photos Online',
    subhead:
      'Make your photos bigger and clearer. Upload a portrait, product shot, or memory and enlarge it in seconds.',
    index: true,
    uploadLabel: 'Upload Photo',
    angle: 'photo',
  },
  {
    path: '/enlarge-image-without-losing-quality',
    label: 'Without Losing Quality',
    title: 'Enlarge Image Without Losing Quality — Free Online Tool',
    description:
      'Learn how to enlarge images without losing quality and try our free AI image enlarger. No sign-up. Free outputs include a watermark; Pro is watermark-free. Pro users get 4× HD output.',
    h1: 'Enlarge Image Without Losing Quality',
    subhead:
      'Understand why images lose quality when enlarged — and how AI upscaling can help preserve detail. Try the tool for free.',
    index: true,
    uploadLabel: 'Try Free Enlarger',
    angle: 'quality',
  },
  {
    path: '/pricing',
    label: 'Pricing',
    title: 'Pricing — Image Enlarger Pro Plans',
    description:
      'Compare Image Enlarger plans. Free $0, Pro Monthly $9.90, Pro Yearly $94.80. Upgrade for 4× upscales and high-resolution output.',
    h1: 'Image Enlarger Pro Pricing',
    subhead:
      'Start free. Upgrade when you need more resolution, more images, or larger upscales. No hidden fees.',
    index: true,
    uploadLabel: 'Start Free',
  },
  {
    path: '/faq',
    label: 'FAQ',
    title: 'FAQ — Image Enlarger Help, Plans & How It Works',
    description:
      'Find answers about Image Enlarger: free limits, supported formats, Pro plans, privacy, 4K/8K upscaling, and how the AI image upscaler works.',
    h1: 'Frequently Asked Questions',
    subhead:
      'Quick answers about using Image Enlarger, our free plan, Pro features, and how your images are handled.',
    index: true,
    uploadLabel: 'Try Image Enlarger',
  },
];

export const LEGAL_PAGES: PageDef[] = [
  {
    path: '/privacy',
    label: 'Privacy',
    title: 'Privacy Policy — Image Enlarger Data & Image Handling',
    description: 'Read the Image Enlarger privacy policy: how uploaded images, device identifiers, server logs, and support data are handled.',
    h1: 'Privacy Policy',
    subhead: '',
    index: false,
    uploadLabel: 'Back to Home',
  },
  {
    path: '/terms',
    label: 'Terms',
    title: 'Terms of Service — Image Enlarger Acceptable Use & Refunds',
    description: 'Read the Image Enlarger terms of service covering acceptable use, intellectual property, free and Pro plans, disclaimers, and contact information.',
    h1: 'Terms of Service',
    subhead: '',
    index: false,
    uploadLabel: 'Back to Home',
  },
  {
    path: '/contact',
    label: 'Contact',
    title: 'Contact Image Enlarger — Support, Feedback & Help',
    description: 'Contact Image Enlarger support for questions, feedback, or billing help. Find the support email, common answers, and expected response time.',
    h1: 'Contact Us',
    subhead: '',
    index: false,
    uploadLabel: 'Back to Home',
  },
  {
    path: '/404',
    label: '404',
    title: 'Page Not Found — Image Enlarger Free Image Upscaler',
    description: 'The page you were looking for does not exist. Return to Image Enlarger and try the free AI image upscaler.',
    h1: 'Page Not Found',
    subhead: '',
    index: false,
    uploadLabel: 'Back to Home',
  },
];

export const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/free-image-upscaler', label: 'Free Upscaler' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

export const FOOTER_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/contact', label: 'Contact' },
  { to: '/faq', label: 'FAQ' },
  { to: '/pricing', label: 'Pricing' },
];
